import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import TomSelect from "tom-select"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"
import { getCsrfToken } from "shared/form_helpers"
import { allProjectsStore } from "../stores/all_projects_store"
import { allUsersStore } from "../stores/organization/all_users_store"
import { allLocalesStore } from "../stores/locales/all_locales_store"
import { removeLocaleWildcardSuffix } from "../lib/shared/locales/locale_id_helpers"
import { disposeTooltips, initializeTooltips } from "../lib/shared/tooltip"

/**
 * ProjectsManagementController
 *
 * Drives the admin-only Projects management page (app/views/projects/manage.html.erb).
 *
 * This controller CONSUMES the shared allProjectsStore rather than fetching the project
 * list itself. The org-wide project list is loaded and paginated by the autoLoad supplier
 * (suppliers--projects--projects-supplier), which accumulates every page into
 * allProjectsStore. When the store updates, onAllProjectsStoreUpdate renders/refreshes the
 * table rows here.
 *
 * Per-row member count: the list endpoint does not carry a member count and the supplier
 * chose lazy per-row fetching (see projects_supplier_controller.js). For each rendered row
 * we issue a lightweight GET /api/projects/:project_id/members and cache the member USER IDS.
 * The displayed count is the size of the UNION of those member ids and the non-suspended org
 * admins (admins have org-wide access and are always listed in the edit modal), deduped by
 * user id — so the column matches exactly who the modal's members panel shows, and an admin
 * who is also a member counts once. The admin set comes from _nonSuspendedAdminRows (shared
 * with the modal). Rows render immediately with a "…" placeholder and update when the fetch
 * resolves, so a slow or failing member fetch never blocks the row; when allUsersStore
 * loads/refreshes the already-rendered count cells are re-rendered in place (no re-fetch).
 *
 * Facilities selectors: BOTH the CREATE and EDIT modals use the shared trees--facility-tree
 * checkbox tree (the same widget as the managed-policies location tree). Each tree has its own
 * controller instance on its editFacilityTree / createFacilityTree element, reads the org-scoped
 * allLocalesStore directly (fed by suppliers--locales--all-locales-supplier), and dispatches
 * trees--facility-tree:selectionChanged with '!'-joined locale paths captured by
 * onEditFacilitiesChanged / onCreateFacilitiesChanged into this._editFacilities /
 * this._createFacilities. An "All Facilities" checkbox on each modal selects the '*' wildcard and
 * disables the corresponding tree. The all-locales supplier is given a server-minted
 * organization-scope token on the management page (see ProjectsController#manage +
 * manage.html.erb), so the locale namespace spans the WHOLE organization (scope: :organization,
 * Req 3.2/5.1), not the admin's active project. The CREATE tree starts empty every time the
 * create modal opens (fresh create); the EDIT tree is seeded from the project's current locales.
 *
 * Open project details (Task 5.4): when the edit modal is opened for a project, this
 * controller seeds the edit form from GET /api/projects/describe?id=… (name, description, and
 * the current locales) and loads the members panel from GET /api/projects/:project_id/members.
 * The project's current locales are stashed on the instance (this._editLocales, for the Req 5.7
 * unchanged-locales comparison) and used to seed the facilities checkbox tree (Task 13.2): the
 * tree's current-selections-value is set from the current locales, or — when the locales are
 * exactly ['*'] — the "All Facilities" checkbox is checked and the tree disabled. Both the
 * describe/members loading and all mutations are owned by this management controller, not the
 * supplier.
 *
 * The members API returns member rows carrying only user_id (no name/email — see
 * Api::Projects::MembersController#format_member), so member display names are resolved by
 * joining member user_id against allUsersStore (fed by suppliers--organization--users-supplier).
 * If a user is not found in the store (e.g. it has not loaded yet), the row falls back to showing
 * the raw user_id. When the users store updates while the edit modal is open, the currently
 * rendered members panel is re-rendered so labels fill in.
 *
 * Member multi-selects (Task 5.6): the create and edit forms each have a store-backed TomSelect
 * member widget (never a plain <select>), modeled after project_setup_controller.js. Both are
 * populated from the shared allUsersStore (fed by suppliers--organization--users-supplier, wired
 * on the management view with auto-load). The CREATE form is a multi-select (maxItems null) that
 * EXCLUDES all admins (admins can't be removed once added, and the creating admin is auto-added
 * anyway) — exactly like project_setup_controller.js#_renderMembers. The EDIT form add-member
 * control is a single-select (maxItems 1) used to pick one user to add; its options EXCLUDE all
 * admins AND users already members of the project being edited (derived from
 * this._editMembers.members), refreshed whenever the members panel loads/changes. Both option
 * sets are rebuilt (preserving selection) when allUsersStore updates.
 *
 * Create / edit / member / delete mutations (Tasks 6-10) are only stubbed here — the stub
 * methods open the relevant Bootstrap modal so the data-action wiring in the view resolves
 * without errors. Their full flows are implemented in later tasks.
 *
 * Connects to data-controller="projects-management"
 */
export default class extends Controller {
    static stores = [
        allProjectsStore,
        allUsersStore,
        allLocalesStore
    ]

    static targets = [
        "loadingIndicator",
        "table",
        "tableBody",
        "emptyState",
        "pagination",
        "paginationList",
        // Modal targets — used by the stub open methods below (full logic in Tasks 6-10)
        "createModal",
        "editModal",
        "deleteModal",
        "editProjectIdInput",
        "deleteProjectIdInput",
        "deleteProjectName",
        // Create-form inputs + submit (Task 6)
        "createNameInput",
        "createDescriptionInput",
        "createSubmitButton",
        // Create-form facilities checkbox tree (mirrors the edit tree, Task 13.2): the shared
        // trees--facility-tree widget. createFacilityTree is the tree's controller element
        // (carries data-controller="trees--facility-tree"); the "All Facilities" checkbox selects
        // the '*' wildcard and disables the tree.
        "createFacilityTree",
        "createAllFacilitiesCheckbox",
        // Create-form members multi-select (Task 5.6): store-backed TomSelect (org users,
        // excluding the current admin)
        "createMembersSelect",
        // Edit-details targets (Task 5.4): seeded from GET /api/projects/describe
        "editNameInput",
        "editDescriptionInput",
        // Edit-form facilities checkbox tree (Task 13.2): the shared trees--facility-tree
        // widget replaces the former locales TomSelect. editFacilityTree is the tree's
        // controller element (carries data-controller="trees--facility-tree"); the
        // "All Facilities" checkbox selects the '*' wildcard and disables the tree.
        "editFacilityTree",
        "editAllFacilitiesCheckbox",
        // Edit-form facilities loading placeholder: shown (and the tree + Save disabled) until the
        // FULL locale hierarchy is fetched (allLocalesStore.initialLoadComplete). The tree stays
        // hidden behind the placeholder until every level is loaded, then reveals once with the
        // project's complete selection painted (no region-first pop-in / content shift).
        "editFacilitiesLoading",
        // Edit-form submit button (Task 7): disabled/relabeled while a save is in flight.
        "editSubmitButton",
        // Members panel targets (Task 5.4): populated from GET /api/projects/:id/members
        "membersList",
        "membersEmptyState",
        // Edit-form add-member select (Task 5.6): store-backed TomSelect (org users,
        // excluding the project's current members)
        "addMemberSelect"
    ]

    static values = {
        currentUserId: String,
        pageSize: { type: Number, default: 10 },
        // The admin's currently-active project id (session[:active_project_id]), forwarded from
        // the server on the page root element (Task 10, Req 7.7). Used only to detect whether the
        // project being deleted is the active one; when it is, deletion redirects to
        // /projects/select which clears the session context (ProjectContext#clear_active_project
        // semantics) and prompts project selection. Blank when no active project is set.
        activeProjectId: String
    }

    connect() {
        useStore(this)

        // Client-side paging state over the accumulated store rows.
        this._currentPage = 1
        // Cache of resolved member USER IDS keyed by project id, so re-renders (e.g. pagination,
        // or when allUsersStore loads) don't re-fetch members. Ids (not a bare count) are cached
        // so the displayed count can be deduped against org admins — see _memberCountFor.
        this._memberUserIds = this._memberUserIds || {}

        // Members currently shown in the edit modal's members panel. Kept so the panel can be
        // re-rendered (e.g. when allUsersStore loads and member display names become resolvable)
        // without re-fetching. { projectId, members: [...] } or null when the modal is closed.
        this._editMembers = null

        // The project's current locales, stashed when the edit modal opens (Task 5.4) so the
        // unchanged-locales no-fan-out comparison (Req 5.7) can run against the seeded values.
        this._editLocales = this._editLocales || []

        // The edit facilities checkbox-tree's current selections (Task 13.2). Captured from the
        // trees--facility-tree:selectionChanged event ('!'-joined locale paths). Seeded to the
        // project's current locales when the edit modal opens; used as the locales array on save.
        this._editFacilities = this._editFacilities || []

        // The create facilities checkbox-tree's current selections (mirrors _editFacilities).
        // Captured from the create tree's trees--facility-tree:selectionChanged event; used as the
        // locales array on create. Reset to empty every time the create modal opens.
        this._createFacilities = this._createFacilities || []

        // Store-backed TomSelect member selects (Task 5.6), modeled after
        // project_setup_controller.js. The create-form widget is a multi-select excluding the
        // current admin; the edit-form add-member widget is a single-select excluding current
        // members. Created here and destroyed in disconnect(); options come from allUsersStore.
        this._initMemberSelects()

        // Render whatever the stores already hold (the suppliers may have loaded before this
        // controller connected). Store updates handle later async arrivals.
        this.render(this.allProjectsStoreValue)
        this._renderMemberOptions(this.allUsersStoreValue)
    }

    disconnect() {
        if (this.createMembersTomSelect) {
            this.createMembersTomSelect.destroy()
            this.createMembersTomSelect = null
        }
        if (this.addMemberTomSelect) {
            this.addMemberTomSelect.destroy()
            this.addMemberTomSelect = null
        }
        // Dispose any member-row tooltips (admin remove-button wrappers) so Bootstrap instances
        // don't leak when the controller is torn down (e.g. Turbo navigation).
        if (this.hasMembersListTarget) {
            disposeTooltips(this.membersListTarget)
        }
    }

    // --- Store callbacks (fire when suppliers finish/refresh their fetch) ---

    onAllProjectsStoreUpdate(projects) {
        // A fresh store load may drop/replace projects; keep the current page in range.
        this.render(projects)
    }

    onAllUsersStoreUpdate(users) {
        // Member rows label from allUsersStore and now also INCLUDE every non-suspended org admin
        // (display only — admins can't be removed). When the users store arrives/refreshes while
        // the edit modal is open, re-render so names fill in and admin rows appear.
        if (this._editMembers && Array.isArray(this._editMembers.members)) {
            this._renderMembers(this._editMembers.members)
        }

        // Rebuild both member selects' options from the refreshed org users (Task 5.6),
        // preserving each widget's current selection. Both widgets exclude ALL admins; the
        // add-member widget additionally excludes the project's current members.
        this._renderMemberOptions(users)

        // The table's Members count now includes org admins (union with each project's members),
        // so a change in the admin set must re-render the count cells for currently-rendered rows.
        // Update cells in place using the already-cached member ids — do NOT re-fetch members.
        this._refreshRenderedMemberCountCells()
    }

    /**
     * Re-render the Members count cell for every project row currently in the table, using the
     * cached member ids (no re-fetch). Called when allUsersStore changes so the admin component
     * of the union is reflected. Rows whose member ids haven't been fetched yet keep their
     * placeholder (_memberCountFor returns null) and are filled in by _ensureMemberCount.
     */
    _refreshRenderedMemberCountCells() {
        if (!this.hasTableBodyTarget) {
            return
        }
        this.tableBodyTarget.querySelectorAll("tr[data-project-id]").forEach((row) => {
            const projectId = row.getAttribute("data-project-id")
            if (projectId) {
                this._updateMemberCountCell(projectId, this._memberCountFor({ id: projectId }))
            }
        })
    }

    onAllLocalesStoreUpdate(newValue) {
        // The CREATE and EDIT facilities selectors are shared trees--facility-tree checkbox trees
        // (Task 13.2) that read allLocalesStore directly via their own controller instances, so
        // this management controller does not rebuild any locale options here.
        //
        // It does, however, gate the EDIT modal on the locale load: the facility tree cannot
        // reflect a project's seeded selections until regions are available, so while loading we
        // show a placeholder and disable the tree + Save (set in openEditModal). As soon as
        // REGIONS load (regionsLoaded) we clear that loading gate so the tree becomes interactive:
        // the tree renders at the region level, deeper levels (buildings/areas/rooms) stream in
        // from the background crawl, and any level not yet loaded is fetched on demand when a node
        // is expanded (and fetchMissingSelectionDepth pulls missing ancestor levels for seeded
        // selections). No need to wait for the full initialLoadComplete crawl.
        //
        // We deliberately do NOT re-apply the seeded selections from here. The tree owns that:
        // when a seed lands before the store is ready, applySeededSelections records
        // _pendingSelectionRender and the tree's own onAllLocalesStoreUpdate flushes it. Re-setting
        // its current-selections-value from here would blank the selection for a microtask (an
        // empty-checkbox flicker) and duplicate fetchMissingSelectionDepth. If
        // _pendingSelectionRender is ever removed from the tree, cold-start seeds will silently
        // stop painting — fix it there, not here.
        //
        // Gate on initialLoadComplete (the FULL hierarchy), not regionsLoaded: keep the loading
        // placeholder up and the tree hidden until every level (regions → buildings → areas →
        // rooms) has been fetched, so the tree reveals once with the project's complete selection
        // already painted instead of popping in region-first and shifting as deeper levels stream.
        if (newValue && newValue.initialLoadComplete && this._editFacilitiesLoading) {
            this._setEditFacilitiesLoading(false)
        }
    }

    /**
     * Ask the edit modal's facility tree to collapse back to its minimized state. The tree owns
     * its own expansion state, so this reaches its controller instance and calls the public
     * collapseAll() rather than poking at its internals.
     */
    _collapseEditFacilityTree() {
        if (!this.hasEditFacilityTreeTarget) {
            return
        }

        const tree = this.application.getControllerForElementAndIdentifier(
            this.editFacilityTreeTarget,
            'trees--facility-tree'
        )

        if (tree && typeof tree.collapseAll === 'function') {
            tree.collapseAll()
        }
    }

    /**
     * Minimize the CREATE modal's facility tree, mirroring _collapseEditFacilityTree. The tree
     * controller instance persists across a Bootstrap modal's hide/show (it never reconnects), so
     * expansions explored during one open would otherwise carry into the next. openCreateModal
     * calls this BEFORE showing the modal so the collapse is never visible as a flash.
     */
    _collapseCreateFacilityTree() {
        if (!this.hasCreateFacilityTreeTarget) {
            return
        }

        const tree = this.application.getControllerForElementAndIdentifier(
            this.createFacilityTreeTarget,
            'trees--facility-tree'
        )

        if (tree && typeof tree.collapseAll === 'function') {
            tree.collapseAll()
        }
    }

    // --- Edit facilities checkbox tree (Task 13.2) ---
    //
    // The edit modal's facilities selector is the shared trees--facility-tree checkbox tree
    // (replacing the former locales TomSelect). The tree has its own controller instance on the
    // editFacilityTree element; it reads allLocalesStore and dispatches
    // trees--facility-tree:selectionChanged with detail.selections ('!'-joined locale paths).
    // This controller captures those selections into this._editFacilities and uses them as the
    // project's locales on save. An "All Facilities" checkbox selects the '*' wildcard and
    // disables the tree.

    /**
     * Capture the facility tree's current selections ('!'-joined locale paths) whenever the tree
     * dispatches trees--facility-tree:selectionChanged. Stored as this._editFacilities and used as
     * the locales array in updateProject (unless "All Facilities" is checked, which sends ['*']).
     */
    onEditFacilitiesChanged(event) {
        this._editFacilities = (event && event.detail && event.detail.selections) || []
    }

    /**
     * "All Facilities" toggle (Req 3.12). When checked: the project's locales become ['*'], which
     * SUPERSEDES any specific selections — so the tree's checkboxes are cleared (via its
     * current-selections-value) and the tree is disabled/dimmed. When unchecked: the tree is
     * re-enabled starting empty (the wildcard replaced the prior specific picks); the admin
     * re-selects facilities. updateProject reads ['*'] vs this._editFacilities based on the
     * checkbox state.
     */
    toggleEditAllFacilities(event) {
        const checked = event && event.target ? event.target.checked : this._editAllFacilitiesChecked()

        if (checked) {
            // Clear the tree's selections: setting current-selections-value drives the tree's
            // currentSelectionsValueChanged, which resets its checkboxes and re-renders.
            this._clearEditFacilityTree()
        }

        // Recompute combined disabled state (wildcard OR still-loading) so toggling the wildcard
        // does not accidentally re-enable the tree while facilities are still loading.
        this._refreshEditFacilityTreeDisabled()
    }

    // Clear the edit facility tree's selections by resetting its Stimulus current-selections-value
    // to []. The tree controller's currentSelectionsValueChanged rebuilds its internal set and
    // re-renders (all node checkboxes unchecked). Also clears our captured selection.
    _clearEditFacilityTree() {
        this._editFacilities = []
        if (this.hasEditFacilityTreeTarget) {
            this.editFacilityTreeTarget.setAttribute(
                'data-trees--facility-tree-current-selections-value',
                '[]'
            )
        }
    }

    /** Whether the "All Facilities" checkbox is currently checked. */
    _editAllFacilitiesChecked() {
        return this.hasEditAllFacilitiesCheckboxTarget && this.editAllFacilitiesCheckboxTarget.checked
    }

    /**
     * Disable/dim (or re-enable) the edit facility tree wrapper. Disabling blocks interaction with
     * the tree while "All Facilities" (the '*' wildcard) is active; the tree's internal selections
     * are preserved so unchecking restores them.
     */
    _setEditFacilityTreeDisabled(disabled) {
        if (!this.hasEditFacilityTreeTarget) {
            return
        }
        const wrapper = this.editFacilityTreeTarget
        if (disabled) {
            wrapper.classList.add('disabled')
            wrapper.style.opacity = '0.5'
            wrapper.style.pointerEvents = 'none'
        } else {
            wrapper.classList.remove('disabled')
            wrapper.style.opacity = ''
            wrapper.style.pointerEvents = ''
        }
    }

    /**
     * The edit facility tree is disabled for TWO independent reasons: (1) the full locale
     * hierarchy has not finished loading yet (this._editFacilitiesLoading, cleared on
     * initialLoadComplete), or (2) "All Facilities" (the '*' wildcard) is checked, which
     * supersedes per-node selection. This recomputes the combined state so neither reason clobbers
     * the other — e.g. clearing the loading gate must NOT re-enable the tree if the wildcard is
     * still checked. Call this instead of _setEditFacilityTreeDisabled whenever either reason changes.
     */
    _refreshEditFacilityTreeDisabled() {
        this._setEditFacilityTreeDisabled(this._editFacilitiesLoading || this._editAllFacilitiesChecked())
    }

    /**
     * Toggle the edit-modal "facilities still loading" gate. While loading: show the placeholder,
     * disable the tree, and disable Save (the tree cannot show the project's seeded selections,
     * and saving could persist an incomplete facility set). When loading clears: hide the
     * placeholder, restore the tree's disabled state per the All Facilities wildcard, and enable
     * Save (unless a save is already in flight). Only takes effect while the edit modal is open.
     */
    _setEditFacilitiesLoading(loading) {
        this._editFacilitiesLoading = loading

        if (this.hasEditFacilitiesLoadingTarget) {
            this.editFacilitiesLoadingTarget.classList.toggle('d-none', !loading)
        }

        // Keep the tree itself hidden while loading so the placeholder is the only facilities UI.
        if (this.hasEditFacilityTreeTarget) {
            this.editFacilityTreeTarget.classList.toggle('d-none', loading)
        }

        this._refreshEditFacilityTreeDisabled()

        if (loading) {
            this._disableEditSubmitForLoading()
        } else {
            this._enableEditSubmitAfterLoading()
        }
    }

    /** Disable Save while facilities are loading (no "Saving..." relabel — that is save-in-flight). */
    _disableEditSubmitForLoading() {
        if (this.hasEditSubmitButtonTarget) {
            this.editSubmitButtonTarget.disabled = true
        }
    }

    /** Re-enable Save once facilities finish loading, unless a save is currently in flight. */
    _enableEditSubmitAfterLoading() {
        if (this.hasEditSubmitButtonTarget && !this._editSaveInFlight) {
            this.editSubmitButtonTarget.disabled = false
        }
    }

    // --- Create facilities checkbox tree (mirrors the edit tree, Task 13.2) ---
    //
    // The create modal's facilities selector is the shared trees--facility-tree checkbox tree,
    // identical in behavior to the edit tree but with create-specific targets/state. The tree has
    // its own controller instance on the createFacilityTree element; it reads allLocalesStore and
    // dispatches trees--facility-tree:selectionChanged with detail.selections ('!'-joined locale
    // paths). This controller captures those selections into this._createFacilities and uses them
    // as the project's locales on create. An "All Facilities" checkbox selects the '*' wildcard
    // and disables the tree. The tree starts empty every time the create modal opens.

    /**
     * Capture the create facility tree's current selections ('!'-joined locale paths) whenever the
     * tree dispatches trees--facility-tree:selectionChanged. Stored as this._createFacilities and
     * used as the locales array in createProject (unless "All Facilities" is checked → ['*']).
     */
    onCreateFacilitiesChanged(event) {
        this._createFacilities = (event && event.detail && event.detail.selections) || []
    }

    /**
     * "All Facilities" toggle for the create modal (Req 3.12). When checked: the project's locales
     * become ['*'], so the tree's checkboxes are cleared (via its current-selections-value) and
     * the tree is disabled/dimmed. When unchecked: the tree is re-enabled starting empty.
     * createProject reads ['*'] vs this._createFacilities based on the checkbox state.
     */
    toggleCreateAllFacilities(event) {
        const checked = event && event.target ? event.target.checked : this._createAllFacilitiesChecked()

        if (checked) {
            this._clearCreateFacilityTree()
        }

        this._setCreateFacilityTreeDisabled(checked)
    }

    // Clear the create facility tree's selections by resetting its Stimulus current-selections-value
    // to []. The tree controller's currentSelectionsValueChanged rebuilds its internal set and
    // re-renders (all node checkboxes unchecked). Also clears our captured selection.
    _clearCreateFacilityTree() {
        this._createFacilities = []
        if (this.hasCreateFacilityTreeTarget) {
            this.createFacilityTreeTarget.setAttribute(
                'data-trees--facility-tree-current-selections-value',
                '[]'
            )
        }
    }

    /** Whether the create "All Facilities" checkbox is currently checked. */
    _createAllFacilitiesChecked() {
        return this.hasCreateAllFacilitiesCheckboxTarget && this.createAllFacilitiesCheckboxTarget.checked
    }

    /**
     * Disable/dim (or re-enable) the create facility tree wrapper. Mirrors
     * _setEditFacilityTreeDisabled.
     */
    _setCreateFacilityTreeDisabled(disabled) {
        if (!this.hasCreateFacilityTreeTarget) {
            return
        }
        const wrapper = this.createFacilityTreeTarget
        if (disabled) {
            wrapper.classList.add('disabled')
            wrapper.style.opacity = '0.5'
            wrapper.style.pointerEvents = 'none'
        } else {
            wrapper.classList.remove('disabled')
            wrapper.style.opacity = ''
            wrapper.style.pointerEvents = ''
        }
    }

    // --- Member selects (Task 5.6) ---
    //
    // Store-backed TomSelect widgets on the create and edit forms, modeled after
    // project_setup_controller.js#_renderMembers. Options come from the shared allUsersStore (fed
    // by suppliers--organization--users-supplier). Values are user ids; labels are "name (email)".
    //   - createMembersSelect: multi-select (maxItems null) EXCLUDING the current admin, who is
    //     auto-added as a member on create.
    //   - addMemberSelect:      single-select (maxItems 1) used to pick one user to add in the
    //     edit modal, EXCLUDING users already members of the project being edited.

    /**
     * Initialize TomSelect on the create members multi-select (maxItems null, remove_button,
     * create disabled — like project_setup_controller.js) and the edit add-member single-select
     * (maxItems 1, create disabled). Destroyed in disconnect().
     */
    _initMemberSelects() {
        if (this.hasCreateMembersSelectTarget && !this.createMembersTomSelect) {
            this.createMembersTomSelect = new TomSelect(this.createMembersSelectTarget, {
                plugins: ['remove_button'],
                placeholder: 'Select members to add',
                create: false,
                maxItems: null
            })
        }

        if (this.hasAddMemberSelectTarget && !this.addMemberTomSelect) {
            this.addMemberTomSelect = new TomSelect(this.addMemberSelectTarget, {
                placeholder: 'Select a member to add',
                create: false,
                maxItems: 1
            })
        }
    }

    /**
     * Rebuild both member selects' options from the org users store, preserving each widget's
     * current selection. The create widget excludes the current admin; the add-member widget
     * excludes the project's current members.
     */
    _renderMemberOptions(usersValue) {
        this._renderCreateMemberOptions(usersValue)
        this._renderAddMemberOptions(usersValue)
    }

    /**
     * Populate the create-form members multi-select from the users store, EXCLUDING ALL admins,
     * preserving the current selection. Admins have org-wide project access and can't be removed,
     * so offering them as members is pointless and would create an unremovable membership row.
     * (This also covers the creating admin, who is auto-added on create.) Mirrors
     * project_setup_controller.js#_renderMembers.
     */
    _renderCreateMemberOptions(usersValue) {
        if (!this.createMembersTomSelect) {
            return
        }

        const users = Array.isArray(usersValue) ? usersValue : []

        const selected = this.createMembersTomSelect.getValue()

        this.createMembersTomSelect.clearOptions()

        let order = 0
        users.forEach((user) => {
            if (!user || !user.id) {
                return
            }
            // Exclude every admin (not just the current admin) — admins are never offerable members.
            if (user.admin) {
                return
            }
            this.createMembersTomSelect.addOption({
                value: user.id,
                text: this._userLabel(user),
                $order: order++
            })
        })

        this.createMembersTomSelect.refreshOptions(false)

        if (selected && selected.length) {
            this.createMembersTomSelect.setValue(selected, true)
        }
    }

    /**
     * Populate the edit-form add-member single-select from the users store, EXCLUDING users who
     * are already members of the project being edited (this._editMembers.members) AND ALL admins,
     * preserving the current selection. Admins can't be removed once added, so they are never
     * offerable. Refreshed whenever the members panel loads/changes so the option set stays in
     * sync with the current membership.
     */
    _renderAddMemberOptions(usersValue) {
        if (!this.addMemberTomSelect) {
            return
        }

        const users = Array.isArray(usersValue) ? usersValue : []

        // Ids of the project's current members (if the edit modal is open); those users should
        // not be offered as candidates to add.
        const currentMemberIds = new Set(
            (this._editMembers && Array.isArray(this._editMembers.members) ? this._editMembers.members : [])
                .map((member) => (member && member.user_id != null ? String(member.user_id) : null))
                .filter((id) => id != null)
        )

        const selected = this.addMemberTomSelect.getValue()

        this.addMemberTomSelect.clearOptions()

        let order = 0
        users.forEach((user) => {
            if (!user || !user.id) {
                return
            }
            // Exclude every admin — admins can't be removed, so adding one is pointless.
            if (user.admin) {
                return
            }
            if (currentMemberIds.has(String(user.id))) {
                return
            }
            this.addMemberTomSelect.addOption({
                value: user.id,
                text: this._userLabel(user),
                $order: order++
            })
        })

        this.addMemberTomSelect.refreshOptions(false)

        // Restore the prior selection only if it is still a valid (non-member) option.
        if (selected && selected.length && !currentMemberIds.has(String(selected))) {
            this.addMemberTomSelect.setValue(selected, true)
        }
    }

    /**
     * Build a display label for a user option: "name (email)" when both are present, otherwise
     * whichever is available, falling back to the id. Matches the labeling in
     * project_setup_controller.js and _memberLabel.
     */
    _userLabel(user) {
        if (!user) {
            return ''
        }
        if (user.name && user.email) {
            return `${ user.name } (${ user.email })`
        }
        return user.name || user.email || String(user.id || '')
    }

    // --- Rendering ---

    render(projects) {
        // Sort by name (case-insensitive) before paging, so page boundaries follow the displayed
        // order rather than whatever order the supplier accumulated its pages in. Copy first:
        // sorting in place would mutate allProjectsStore's own array.
        const all = (Array.isArray(projects) ? projects : [])
            .slice()
            .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))

        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.style.display = "none"
        }

        // Empty state
        if (all.length === 0) {
            if (this.hasTableBodyTarget) {
                this.tableBodyTarget.innerHTML = ""
            }
            if (this.hasTableTarget) {
                this.tableTarget.style.display = "none"
            }
            if (this.hasEmptyStateTarget) {
                this.emptyStateTarget.style.display = "block"
            }
            this._renderPagination(0)
            return
        }

        if (this.hasTableTarget) {
            this.tableTarget.style.display = ""
        }
        if (this.hasEmptyStateTarget) {
            this.emptyStateTarget.style.display = "none"
        }

        const pageSize = this._pageSize()
        const totalPages = Math.max(1, Math.ceil(all.length / pageSize))
        if (this._currentPage > totalPages) {
            this._currentPage = totalPages
        }
        if (this._currentPage < 1) {
            this._currentPage = 1
        }

        const start = (this._currentPage - 1) * pageSize
        const pageProjects = all.slice(start, start + pageSize)

        if (this.hasTableBodyTarget) {
            this.tableBodyTarget.innerHTML = pageProjects.map(p => this._rowHtml(p)).join("")
        }

        this._renderPagination(all.length)

        // Kick off lazy member-count fetches for the rows just rendered.
        pageProjects.forEach(p => this._ensureMemberCount(p))
    }

    _rowHtml(project) {
        const id = project.id || ""
        const name = this._escapeHtml(project.name || "")
        const description = this._escapeHtml(project.description || "")

        // The Members count is the union of this project's members and the org admins (see
        // _memberCountFor). Null (ids not yet fetched) renders a placeholder that updates on
        // resolve / when allUsersStore loads.
        const memberCount = this._memberCountFor(project)
        const memberCell = memberCount == null ? "…" : String(memberCount)

        return `
            <tr data-project-id="${ this._escapeAttr(id) }">
                <td>${ name }</td>
                <td>${ description }</td>
                <td data-projects-management-member-count-for="${ this._escapeAttr(id) }">${ memberCell }</td>
                <td>
                    <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                            data-action="click->projects-management#openEditModal"
                            data-project-id="${ this._escapeAttr(id) }">
                        <i class="bi bi-pencil"></i> Edit
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-danger"
                            data-action="click->projects-management#openDeleteModal"
                            data-project-id="${ this._escapeAttr(id) }"
                            data-project-name="${ this._escapeAttr(project.name || "") }">
                        <i class="bi bi-trash"></i> Delete
                    </button>
                </td>
            </tr>
        `
    }

    // --- Pagination (client-side, over accumulated store rows) ---

    _renderPagination(totalCount) {
        if (!this.hasPaginationTarget || !this.hasPaginationListTarget) {
            return
        }

        const pageSize = this._pageSize()
        const totalPages = Math.ceil(totalCount / pageSize)

        if (totalPages <= 1) {
            this.paginationTarget.classList.add("d-none")
            this.paginationListTarget.innerHTML = ""
            return
        }

        this.paginationTarget.classList.remove("d-none")

        const items = []

        // Previous
        items.push(this._pageItem(this._currentPage - 1, "Previous", this._currentPage === 1, false))

        for (let page = 1; page <= totalPages; page++) {
            items.push(this._pageItem(page, String(page), false, page === this._currentPage))
        }

        // Next
        items.push(this._pageItem(this._currentPage + 1, "Next", this._currentPage === totalPages, false))

        this.paginationListTarget.innerHTML = items.join("")
    }

    _pageItem(page, label, disabled, active) {
        const liClass = ["page-item"]
        if (disabled) {
            liClass.push("disabled")
        }
        if (active) {
            liClass.push("active")
        }

        const attrs = disabled
            ? 'tabindex="-1" aria-disabled="true"'
            : `data-action="click->projects-management#goToPage" data-page="${ page }"`

        return `
            <li class="${ liClass.join(" ") }">
                <a class="page-link" href="#" ${ attrs }>${ this._escapeHtml(label) }</a>
            </li>
        `
    }

    goToPage(event) {
        event.preventDefault()
        const page = parseInt(event.currentTarget.dataset.page, 10)
        if (Number.isNaN(page)) {
            return
        }
        this._currentPage = page
        this.render(this.allProjectsStoreValue)
    }

    _pageSize() {
        const size = this.hasPageSizeValue ? this.pageSizeValue : 10
        return size > 0 ? size : 10
    }

    // --- Lazy per-row member count ---

    /**
     * Return an already-known member count for a project (batched on the stored object or
     * previously fetched/cached), or null if it still needs to be resolved.
     */
    /**
     * The Members count shown in the table: the size of the UNION of this project's member user
     * ids (lazily fetched, cached in this._memberUserIds) and the non-suspended org admins — the
     * exact people the edit modal's members panel lists (admins have org-wide access). Deduped by
     * user id, so an admin who is also a member counts once. Returns null (→ "…" placeholder)
     * until the per-row members fetch has populated the cached ids; admins alone are not shown as
     * the count before the member ids are known, so the number never briefly undercounts members.
     * When allUsersStore has not loaded, the union is just the cached member ids (today's count).
     */
    _memberCountFor(project) {
        if (!project) {
            return null
        }
        const memberIds = this._memberUserIds[project.id]
        if (!Array.isArray(memberIds)) {
            return null
        }

        const union = new Set(memberIds.map((id) => String(id)))
        this._nonSuspendedAdminRows().forEach((row) => union.add(row.user_id))
        return union.size
    }

    /**
     * Ensure this project's member user ids are fetched (once) so the Members count can be
     * computed as the union of members + non-suspended admins. Fetches
     * GET /api/projects/:project_id/members and caches the member user ids; only that row's cell
     * is updated so a slow or failing request never blocks rendering.
     */
    async _ensureMemberCount(project) {
        if (!project || !project.id) {
            return
        }
        // Member ids already cached — just (re)render the combined count (admins may have loaded).
        if (Array.isArray(this._memberUserIds[project.id])) {
            this._updateMemberCountCell(project.id, this._memberCountFor(project))
            return
        }

        try {
            const response = await fetch(`/api/projects/${ encodeURIComponent(project.id) }/members`, {
                method: "GET",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                },
                credentials: "same-origin"
            })

            if (!response.ok) {
                this._updateMemberCountCell(project.id, "—")
                return
            }

            const json = await response.json()
            const payload = json && json.data ? json.data : {}
            if (!Array.isArray(payload.members)) {
                this._updateMemberCountCell(project.id, "—")
                return
            }

            // Cache the member user ids (not a bare number) so the count can be deduped against
            // admins. NOTE: the members API is paginated (default page size 100, see
            // ProjectMemberQueries#describe / Constants::Defaults::MAX_PAGE_SIZE); this single
            // unpaginated fetch reads only the first page, so a project with >100 members would
            // undercount. Paging behavior is unchanged here.
            this._memberUserIds[project.id] = payload.members
                                                     .map((member) => (member && member.user_id != null ? String(member.user_id) : null))
                                                     .filter((id) => id != null)
            this._updateMemberCountCell(project.id, this._memberCountFor(project))
        } catch (error) {
            // Never let a member-count failure block the row; show a neutral marker.
            this._updateMemberCountCell(project.id, "—")
        }
    }

    _updateMemberCountCell(projectId, value) {
        const cell = this.element.querySelector(
            `[data-projects-management-member-count-for="${ this._cssEscape(projectId) }"]`
        )
        // null/undefined (ids not yet fetched) renders the loading placeholder, matching _rowHtml.
        if (cell) {
            cell.textContent = value == null ? "…" : String(value)
        }
    }

    // --- Modal open stubs (full flows implemented in Tasks 6-10) ---
    //
    // These minimal stubs exist so the view's data-action wiring resolves. They only open
    // the corresponding Bootstrap modal (and, for edit/delete, seed the target id). The
    // real create/edit/member/delete logic lives in later tasks.

    openCreateModal() {
        // Reset the create facilities tree to empty and uncheck "All Facilities" BEFORE showing
        // the modal, so each open starts fresh (fresh create — nothing pre-checked).
        if (this.hasCreateAllFacilitiesCheckboxTarget) {
            this.createAllFacilitiesCheckboxTarget.checked = false
        }
        this._clearCreateFacilityTree()
        this._setCreateFacilityTreeDisabled(false)
        // Minimize the tree so expansion explored during a previous open does not carry over.
        // Runs BEFORE the modal is shown (like the edit modal's _resetEditForm), so the collapse
        // is never visible as a flash. _clearCreateFacilityTree above already reset the selection
        // to empty, so there is nothing to re-reveal — the tree opens fully collapsed.
        this._collapseCreateFacilityTree()

        this._showModal(this.hasCreateModalTarget ? this.createModalTarget : null)
    }

    openEditModal(event) {
        const projectId = event?.currentTarget?.dataset?.projectId || ""
        if (this.hasEditProjectIdInputTarget) {
            this.editProjectIdInputTarget.value = projectId
        }

        // Clear any stale details/errors from a previous edit before loading fresh data.
        this._resetEditForm()

        this._showModal(this.hasEditModalTarget ? this.editModalTarget : null)

        if (projectId) {
            // Seed the edit view from describe, and load the members panel. Owned by this
            // management controller (not the supplier). Both run independently so a failure in
            // one does not block the other.
            this._loadProjectDetails(projectId)
            this._loadMembers(projectId)
        }
    }

    // --- Edit details loading (Task 5.4) ---

    /**
     * Fetch GET /api/projects/describe?id=:id and seed the edit form (name, description, and
     * the current locales). The describe response is the wrapped success envelope
     * { data: { id, name, description, locales, ... } }.
     *
     * The locales TomSelect widget itself is wired in Task 5.5; here we stash the current
     * locales on the instance (this._editLocales) and, when the underlying <select> exists, set
     * its option values so 5.5 can pick up the seeded selection.
     */
    async _loadProjectDetails(projectId) {
        try {
            const response = await fetch(
                `/api/projects/describe?id=${ encodeURIComponent(projectId) }`,
                {
                    method: "GET",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json"
                    },
                    credentials: "same-origin"
                }
            )

            if (!response.ok) {
                sendFailureNotification("Unable to load project details. Please try again.")
                return
            }

            const json = await response.json()
            const project = json && json.data ? json.data : {}

            if (this.hasEditNameInputTarget) {
                this.editNameInputTarget.value = project.name || ""
            }
            if (this.hasEditDescriptionInputTarget) {
                this.editDescriptionInputTarget.value = project.description || ""
            }

            // Stash the current locales (Req 5.7 unchanged-locales comparison) and seed the
            // facilities checkbox tree from them (Task 13.2). If the project's locales are exactly
            // ['*'], mirror the "All Facilities" checked state (check the box + disable the tree);
            // otherwise seed the tree's selection from the current locales.
            const locales = Array.isArray(project.locales) ? project.locales : []
            this._editLocales = locales
            this._seedEditFacilities(locales)
        } catch (error) {
            sendFailureNotification("A network error occurred loading project details.")
        }
    }

    /**
     * Seed the edit facilities checkbox tree (Task 13.2) from the project's current locales.
     *
     * The trees--facility-tree widget reads its initial selection from its Stimulus Array value
     * `current-selections-value`; setting that attribute on the tree's controller element makes
     * the widget adopt the selection (currentSelectionsValueChanged re-renders the tree). If the
     * project's locales are exactly ['*'] (the wildcard), instead mirror the "All Facilities"
     * checked state: check the box, disable the tree, and seed the tree's selection empty (the
     * effective locales come from the checkbox on save). this._editFacilities is initialized to
     * the seeded values so updateProject reads the correct set even before the tree dispatches.
     *
     * Legacy "path!*" grants (written by lib/tasks/projects_backfill.rake) are normalized to
     * their covering path first: the tree matches selections by EXACT path equality, so a stored
     * "<regionId>!*" would otherwise render neither checked nor badged. The two forms are
     * access-equivalent server-side (remove_locale_wildcard_suffix -> locale_at_or_below? in
     * app/services/mixins/locale_accessibility.rb), so re-saving persists the canonical bare
     * path without changing which facilities the project actually grants.
     */
    _seedEditFacilities(locales) {
        const values = (Array.isArray(locales) ? locales : [])
            .filter((locale) => locale != null)
            .map((locale) => String(locale))

        const isWildcard = values.length === 1 && values[0] === '*'

        // Reflect the "All Facilities" wildcard state on the checkbox + tree.
        if (this.hasEditAllFacilitiesCheckboxTarget) {
            this.editAllFacilitiesCheckboxTarget.checked = isWildcard
        }
        // Combined disabled state (wildcard OR still-loading) so seeding does not re-enable the
        // tree while facilities are still loading.
        this._refreshEditFacilityTreeDisabled()

        // Seed the tree's selection. When the wildcard is active the tree is disabled and seeded
        // empty; otherwise seed it with the project's current locale paths, normalizing any
        // legacy "path!*" grant to its covering path (de-duplicated, since stripping can make a
        // "path" and a "path!*" entry collide) so the tree's exact-match check recognizes them.
        const treeSelections = isWildcard
            ? []
            : [...new Set(values.map(removeLocaleWildcardSuffix))]
        if (this.hasEditFacilityTreeTarget) {
            this.editFacilityTreeTarget.setAttribute(
                'data-trees--facility-tree-current-selections-value',
                JSON.stringify(treeSelections)
            )
        }

        // Initialize the captured selection so a save works even before the tree re-dispatches.
        this._editFacilities = treeSelections
    }

    // --- Members panel loading + rendering (Task 5.4) ---

    /**
     * Fetch GET /api/projects/:project_id/members and render the members panel. The response is
     * the wrapped success envelope { data: { count, members: [{ user_id, ... }], ... } }.
     */
    async _loadMembers(projectId) {
        try {
            const response = await fetch(
                `/api/projects/${ encodeURIComponent(projectId) }/members`,
                {
                    method: "GET",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json"
                    },
                    credentials: "same-origin"
                }
            )

            if (!response.ok) {
                sendFailureNotification("Unable to load project members. Please try again.")
                this._editMembers = { projectId, members: [] }
                this._renderMembers([])
                this._renderAddMemberOptions(this.allUsersStoreValue)
                return
            }

            const json = await response.json()
            const payload = json && json.data ? json.data : {}
            const members = Array.isArray(payload.members) ? payload.members : []

            this._editMembers = { projectId, members }
            this._renderMembers(members)
            // Refresh the add-member candidates to exclude the project's current members (5.6).
            this._renderAddMemberOptions(this.allUsersStoreValue)
        } catch (error) {
            sendFailureNotification("A network error occurred loading project members.")
            this._editMembers = { projectId, members: [] }
            this._renderMembers([])
            this._renderAddMemberOptions(this.allUsersStoreValue)
        }
    }

    /**
     * Render the members panel rows into the membersList target and toggle the empty state.
     * Each row shows the member's display name/email (resolved from allUsersStore by user_id,
     * falling back to the raw user_id) and a remove button wired to removeMember carrying the
     * member's user id and the project id.
     */
    _renderMembers(members) {
        const rows = this._combinedMemberRows(Array.isArray(members) ? members : [])
        const projectId = this._editMembers ? this._editMembers.projectId : ""

        if (this.hasMembersEmptyStateTarget) {
            this.membersEmptyStateTarget.style.display = rows.length === 0 ? "block" : "none"
        }

        if (this.hasMembersListTarget) {
            // Dispose any tooltips from the previous render before replacing the DOM so Bootstrap
            // instances (on the admin remove-button wrappers) don't leak, then initialize tooltips
            // for the new rows. Admin rows carry a [data-bs-toggle="tooltip"] wrapper span.
            disposeTooltips(this.membersListTarget)
            this.membersListTarget.innerHTML = rows.map(m => this._memberRowHtml(m, projectId)).join("")
            initializeTooltips(this.membersListTarget)
        }
    }

    /**
     * Build the members-panel display list: the UNION of (a) every non-suspended org admin from
     * allUsersStore and (b) the project's actual members from the members API. Admins have
     * org-wide project access and can't be removed, so they are shown even when they hold no
     * membership row (display only — this does NOT mutate this._editMembers.members, so the
     * removeMember guard and add-member exclusion keep reading the raw API result).
     *
     * Dedupe by user id: an admin who ALSO has a membership row appears once, as an admin row.
     * Order: admins first (sorted by name case-insensitively, falling back to email), then the
     * remaining API members in their original order. Operators never reach allUsersStore (the
     * users list API rejects them — Api::UserController#list, ADB-299), so no operator filter is
     * needed here. If the users store has not loaded yet there are no admin rows and the API
     * members render as-is; onAllUsersStoreUpdate re-renders once it arrives.
     */
    _combinedMemberRows(apiMembers) {
        // Non-suspended admins (shared with the table's member-count column so they can't drift).
        const adminRows = this._nonSuspendedAdminRows()
        const adminIds = new Set(adminRows.map((row) => row.user_id))

        // API members that are not already represented by an admin row, in their original order.
        const memberRows = apiMembers.filter(
            (member) => member && member.user_id != null && !adminIds.has(String(member.user_id))
        )

        return adminRows.concat(memberRows)
    }

    /**
     * The non-suspended org admins from allUsersStore, as synthetic member rows (shaped like API
     * member rows: { user_id, name, email, admin: true }), sorted by name (case-insensitive,
     * falling back to email). This is the SINGLE source of the "which admins count/display" rule,
     * shared by the edit modal's members panel (_combinedMemberRows) and the table's Members
     * count column (_memberCountFor) so the two can never disagree. Operators never reach
     * allUsersStore (Api::UserController#list rejects them, ADB-299). Empty until the store loads.
     */
    _nonSuspendedAdminRows() {
        const users = Array.isArray(this.allUsersStoreValue) ? this.allUsersStoreValue : []
        return users
            .filter((user) => user && user.id != null && user.admin && !user.suspended)
            .map((user) => ({ user_id: String(user.id), name: user.name, email: user.email, admin: true }))
            .sort((a, b) => {
                const aKey = (a.name || a.email || "").toLowerCase()
                const bKey = (b.name || b.email || "").toLowerCase()
                return aKey.localeCompare(bKey)
            })
    }

    _memberRowHtml(member, projectId) {
        const userId = member && member.user_id != null ? String(member.user_id) : ""
        const label = this._escapeHtml(this._memberLabel(member))
        const isAdmin = !!(member && member.admin)

        // Admins have org-wide project access, so they can't be removed (enforced server-side in
        // Api::Projects::MembersController#delete). Render the remove button disabled. A disabled
        // button does not emit mouse events, so the Bootstrap tooltip is attached to a wrapping
        // span (tabindex="0" so it is still hover/focus reachable) rather than the button itself.
        if (isAdmin) {
            const tooltip = "This user is an admin. Admins have access to every project, so they can't be removed."
            return `
            <tr data-user-id="${ this._escapeAttr(userId) }" data-admin="true">
                <td>${ label } <span class="badge bg-secondary ms-2">Admin</span></td>
                <td>
                    <span class="d-inline-block" tabindex="0"
                          data-bs-toggle="tooltip"
                          data-bs-title="${ this._escapeAttr(tooltip) }">
                        <button type="button" class="btn btn-sm btn-outline-danger" disabled
                                data-user-id="${ this._escapeAttr(userId) }"
                                data-project-id="${ this._escapeAttr(projectId) }">
                            <i class="bi bi-trash"></i> Remove
                        </button>
                    </span>
                </td>
            </tr>
        `
        }

        return `
            <tr data-user-id="${ this._escapeAttr(userId) }">
                <td>${ label }</td>
                <td>
                    <button type="button" class="btn btn-sm btn-outline-danger"
                            data-action="click->projects-management#removeMember"
                            data-user-id="${ this._escapeAttr(userId) }"
                            data-project-id="${ this._escapeAttr(projectId) }">
                        <i class="bi bi-trash"></i> Remove
                    </button>
                </td>
            </tr>
        `
    }

    /**
     * Resolve a member's display label. The members API now returns name/email directly on each
     * member row (see Api::Projects::MembersController#format_member), so prefer those. If they
     * are absent, fall back to joining the member's user_id against the org users store (fed by
     * suppliers--organization--users-supplier), and finally to the raw user id when the user is
     * not present in the store (e.g. it has not loaded yet).
     */
    _memberLabel(member) {
        if (!member) {
            return ""
        }
        const userId = member.user_id != null ? String(member.user_id) : ""

        // Prefer name/email returned directly by the members API.
        if (member.name && member.email) {
            return `${ member.name } (${ member.email })`
        }
        if (member.name || member.email) {
            return member.name || member.email
        }

        // Fall back to the org users store join (may not be loaded yet).
        const users = Array.isArray(this.allUsersStoreValue) ? this.allUsersStoreValue : []
        const user = users.find(u => u && String(u.id) === userId)
        if (user) {
            if (user.name && user.email) {
                return `${ user.name } (${ user.email })`
            }
            return user.name || user.email || userId
        }
        return userId
    }

    _resetEditForm() {
        if (this.hasEditNameInputTarget) {
            this.editNameInputTarget.value = ""
        }
        if (this.hasEditDescriptionInputTarget) {
            this.editDescriptionInputTarget.value = ""
        }
        if (this.hasMembersListTarget) {
            this.membersListTarget.innerHTML = ""
        }
        if (this.hasMembersEmptyStateTarget) {
            this.membersEmptyStateTarget.style.display = "none"
        }
        // Reset the edit facilities checkbox tree (Task 13.2): clear the captured selection, seed
        // the tree's current-selections-value back to empty, uncheck the "All Facilities" box, and
        // re-enable the tree. _loadProjectDetails re-seeds these from the project's locales.
        this._editFacilities = []
        if (this.hasEditFacilityTreeTarget) {
            this.editFacilityTreeTarget.setAttribute(
                'data-trees--facility-tree-current-selections-value',
                '[]'
            )
        }
        if (this.hasEditAllFacilitiesCheckboxTarget) {
            this.editAllFacilitiesCheckboxTarget.checked = false
        }
        // Minimize the tree so exploration from a previous open does not carry over. Runs BEFORE
        // the modal is shown (openEditModal calls this first), so the collapse is never visible as
        // a flash. _loadProjectDetails re-seeds afterwards, and the tree re-expands only the
        // ancestors of the project's actual selections.
        this._collapseEditFacilityTree()
        // Establish the facilities loading gate from the current store state. If REGIONS have not
        // loaded yet, show the placeholder and disable the tree + Save until onAllLocalesStoreUpdate
        // clears it (initialLoadComplete). We gate on the FULL hierarchy, not just regionsLoaded,
        // so the placeholder stays up and the tree stays hidden until every level (regions →
        // buildings → areas → rooms) is fetched. The tree then reveals once with the project's
        // complete selection already painted — no region-first pop-in or content shift as deeper
        // levels stream in. _loadProjectDetails re-seeds selections from the project's locales.
        const localesLoaded = !!(this.allLocalesStoreValue && this.allLocalesStoreValue.initialLoadComplete)
        this._setEditFacilitiesLoading(!localesLoaded)
        // Clear any add-member selection carried over from a previous edit. The option set is
        // rebuilt (to exclude the newly-loaded project's current members) once _loadMembers runs.
        if (this.addMemberTomSelect) {
            this.addMemberTomSelect.clear(true)
        }
        this._editMembers = null
        this._editLocales = []
    }

    openDeleteModal(event) {
        const projectId = event?.currentTarget?.dataset?.projectId || ""
        const projectName = event?.currentTarget?.dataset?.projectName || ""
        if (this.hasDeleteProjectIdInputTarget) {
            this.deleteProjectIdInputTarget.value = projectId
        }
        if (this.hasDeleteProjectNameTarget) {
            this.deleteProjectNameTarget.textContent = projectName
        }
        this._showModal(this.hasDeleteModalTarget ? this.deleteModalTarget : null)
    }

    // --- Create flow (Task 6) ---
    //
    // Modeled after project_setup_controller.js#createProject + #_addMembers:
    //   1. Client-side guards: non-blank name (trim) and >= 1 locale (Req 3.4, 3.5).
    //   2. POST /api/projects with { name, description, locales[] } (CSRF via X-CSRF-Token).
    //      The API creates the Project and auto-adds the creating admin as a member (Req 3.7).
    //   3. For each additional selected member, POST /api/projects/:project_id/members (Req 3.8).
    //   4. Surface wrapped 4xx errors (blank/duplicate name, no locales, invalid locale) as a
    //      failure toast (Req 3.6, 10.1).
    //   5. On success: close the modal, clear the form, show a success toast, and refresh the
    //      store-backed list so the new project appears WITHOUT a full page reload (Req 3.9, 10.3).
    //
    // List refresh strategy: the projects supplier
    // (suppliers--projects--projects-supplier) is mounted on the SAME root element as this
    // controller (see manage.html.erb), so we look it up via the Stimulus application and call
    // its refreshProjects() to re-paginate GET /api/projects/list into allProjectsStore. If that
    // controller instance can't be found (defensive), we fall back to appending the created
    // project to allProjectsStore in place so the row still appears.
    async createProject(event) {
        if (event) {
            event.preventDefault()
        }

        const name = this.hasCreateNameInputTarget ? this.createNameInputTarget.value.trim() : ''
        const description = this.hasCreateDescriptionInputTarget
            ? this.createDescriptionInputTarget.value.trim()
            : ''

        // Client-side guard: non-blank name (Req 3.4).
        if (!name) {
            sendFailureNotification('Project name is required.')
            return
        }

        if (name.length > 256) {
            sendFailureNotification('Project name must be 256 characters or less.')
            return
        }

        // Selected facilities (Task 13.2): when "All Facilities" is checked the project's locales
        // are the ['*'] wildcard; otherwise they are the checkbox tree's selected '!'-joined
        // locale paths captured in this._createFacilities.
        const locales = this._createAllFacilitiesChecked() ? ['*'] : (this._createFacilities || [])

        // Client-side guard: at least one locale (Req 3.5).
        if (!locales || locales.length === 0) {
            sendFailureNotification('Projects require at least one locale.')
            return
        }

        // Additional members to add after create (the creating admin is auto-added server-side).
        const userIds = this.createMembersTomSelect ? this.createMembersTomSelect.getValue() : []

        this._disableCreateSubmit()

        try {
            const response = await fetch('/api/projects', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({ name, description, locales }),
                credentials: 'same-origin'
            })

            if (!response.ok) {
                // Surface the wrapped service error (blank/duplicate name, no locales,
                // invalid locale) as a failure toast (Req 3.6, 10.1).
                const data = await response.json().catch(() => ({}))
                const message = data?.message || data?.errors?.[0]?.message || 'Failed to create project.'
                sendFailureNotification(message)
                this._enableCreateSubmit()
                return
            }

            const data = await response.json()
            const project = data?.data || data

            // Add any additional selected members (Req 3.8). Modeled after
            // project_setup_controller.js#_addMembers: failures are logged but do not abort the
            // create success (the project itself was created and the admin auto-added).
            if (Array.isArray(userIds) && userIds.length > 0 && project && project.id) {
                await this._addMembers(project.id, userIds)
            }

            // Success (Req 3.9, 10.3): close + clear the modal, notify, and refresh the list.
            this._closeCreateModal()
            this._clearCreateForm()

            sendSuccessNotification(`Project "${ project.name || name }" created successfully.`)

            this._refreshProjectsList(project)
        } catch (error) {
            console.error('Error creating project:', error)
            sendFailureNotification('A network error occurred. Please try again.')
        } finally {
            this._enableCreateSubmit()
        }
    }

    /**
     * Add each selected user as a project member (POST /api/projects/:project_id/members).
     * Mirrors project_setup_controller.js#_addMembers: requests run in parallel and a failure is
     * logged (and surfaced as a non-fatal toast) rather than aborting the create success.
     */
    async _addMembers(projectId, userIds) {
        const results = await Promise.allSettled(
            userIds.map(userId =>
                fetch(`/api/projects/${ encodeURIComponent(projectId) }/members`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': getCsrfToken()
                    },
                    body: JSON.stringify({ user_id: userId }),
                    credentials: 'same-origin'
                })
            )
        )

        const failures = results.filter(
            r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.ok)
        )
        if (failures.length > 0) {
            console.warn(`Failed to add ${ failures.length } member(s) to project.`)
            sendFailureNotification(
                `Project created, but ${ failures.length } member(s) could not be added.`
            )
        }
    }

    /**
     * Refresh the store-backed project list so a newly created project appears without a full
     * page reload (Req 3.9, 10.3). Prefers re-triggering the projects supplier (mounted on the
     * same root element) via its refreshProjects(); falls back to appending the created project
     * to allProjectsStore in place when the supplier instance can't be resolved.
     */
    _refreshProjectsList(createdProject) {
        const supplier = this.application.getControllerForElementAndIdentifier(
            this.element,
            'suppliers--projects--projects-supplier'
        )

        if (supplier && typeof supplier.refreshProjects === 'function') {
            supplier.refreshProjects()
            return
        }

        // Fallback: append the created project to the store in place so the row still appears.
        if (createdProject && createdProject.id) {
            const current = Array.isArray(this.allProjectsStoreValue) ? this.allProjectsStoreValue : []
            if (!current.some(p => p && String(p.id) === String(createdProject.id))) {
                this.setAllProjectsStoreValue([...current, createdProject])
            }
        }
    }

    // --- Create-form helpers (Task 6) ---

    _closeCreateModal() {
        const target = this.hasCreateModalTarget ? this.createModalTarget : null
        if (!target) {
            return
        }
        const bs = window.bootstrap
        if (bs && bs.Modal) {
            bs.Modal.getOrCreateInstance(target).hide()
        }
    }

    _clearCreateForm() {
        if (this.hasCreateNameInputTarget) {
            this.createNameInputTarget.value = ''
        }
        if (this.hasCreateDescriptionInputTarget) {
            this.createDescriptionInputTarget.value = ''
        }
        // Reset the create facilities tree back to empty and uncheck "All Facilities" (mirrors the
        // fresh-open reset in openCreateModal) so a subsequent open starts clean.
        if (this.hasCreateAllFacilitiesCheckboxTarget) {
            this.createAllFacilitiesCheckboxTarget.checked = false
        }
        this._clearCreateFacilityTree()
        this._setCreateFacilityTreeDisabled(false)
        if (this.createMembersTomSelect) {
            this.createMembersTomSelect.clear()
        }
    }

    _disableCreateSubmit() {
        if (this.hasCreateSubmitButtonTarget) {
            this.createSubmitButtonTarget.disabled = true
            this.createSubmitButtonTarget.textContent = 'Creating...'
        }
    }

    _enableCreateSubmit() {
        if (this.hasCreateSubmitButtonTarget) {
            this.createSubmitButtonTarget.disabled = false
            this.createSubmitButtonTarget.textContent = 'Create Project'
        }
    }

    // --- Edit flow (Task 7: name/description; Task 8 extends this same method for locales) ---
    //
    // Single-submit for the edit form. Modeled after createProject + project_setup_controller.js:
    //   1. Client-side guards: non-blank name (trim) and <= 256 chars (Req 4.4).
    //   2. PATCH /api/projects/:id with { name, description, locales } (CSRF via X-CSRF-Token).
    //      The full current locales selection is sent so the server can decide fan-out; when
    //      only name/description changed (locales unchanged) the server does NOT fan out and
    //      returns no async_task_id (Req 4.2). This method does not special-case that — it just
    //      sends the fields; the server owns the fan-out decision.
    //   3. Surface wrapped 4xx errors (blank/too-long name, duplicate name) into the edit modal's
    //      error region (Req 4.3, 4.4, 10.1).
    //   4. On success: reflect the new values in the list (refresh the store-backed list) and
    //      close the modal (Req 4.5, 10.3).
    //
    // Task 8 extends this method for the locales path:
    //   - Empty-locales client guard (Req 5.3): if the selection is empty, surface
    //     "Projects require at least one locale" and do NOT submit.
    //   - Unchanged-locales handling (Req 5.7): compare the selected locales to the project's
    //     originally-loaded locales (this._editLocales) order-insensitively. If UNCHANGED, OMIT
    //     `locales` from the PATCH body so the server performs no fan-out (guarantees no
    //     async_task_id). If CHANGED, include the full new locales array (Req 5.2). The server
    //     remains the source of truth — it only fans out when locales actually differ — but
    //     omitting an unchanged set makes a no-op fan-out impossible.
    //   - Async handling (Req 5.5, 5.6, 10.4): when the success response carries an
    //     `async_task_id`, member-permission propagation is in flight. The propagation note is
    //     folded into the page-level success toast; the modal closes on success as usual. When
    //     there is NO async_task_id, Task 7's close-on-success behavior is preserved.
    //   - Invalid-locale server error (Req 5.4) is surfaced as a failure toast by the existing
    //     non-ok error handling below (the wrapped service error), which applies to the locales
    //     path unchanged.
    async updateProject(event) {
        if (event) {
            event.preventDefault()
        }

        // Defense in depth: the Save button is disabled while facilities load, but guard here too
        // so a programmatic submit cannot persist an incomplete facility selection.
        if (this._editFacilitiesLoading) {
            return
        }

        const projectId = this.hasEditProjectIdInputTarget ? this.editProjectIdInputTarget.value : ''
        if (!projectId) {
            sendFailureNotification('Unable to determine which project to update.')
            return
        }

        const name = this.hasEditNameInputTarget ? this.editNameInputTarget.value.trim() : ''
        const description = this.hasEditDescriptionInputTarget
            ? this.editDescriptionInputTarget.value.trim()
            : ''

        // Client-side name guards (Req 4.4): non-blank and <= 256 chars.
        if (!name) {
            sendFailureNotification('Project name is required.')
            return
        }
        if (name.length > 256) {
            sendFailureNotification('Project name must be 256 characters or less.')
            return
        }

        // Current facilities selection (Task 13.2): when "All Facilities" is checked the project's
        // locales are the ['*'] wildcard; otherwise they are the checkbox tree's selected
        // '!'-joined locale paths captured in this._editFacilities.
        const locales = this._editAllFacilitiesChecked() ? ['*'] : (this._editFacilities || [])

        // Empty-facilities client guard (Req 5.3): a project must always have at least one locale.
        // Surface the error and do not submit. (The server enforces this too, but guarding here
        // avoids a pointless round trip and matches the create-flow guard.)
        if (!locales || locales.length === 0) {
            sendFailureNotification('Projects require at least one locale/facility.')
            return
        }

        // Unchanged-locales handling (Req 5.7): compare the selection to the project's
        // originally-loaded locales (stashed as this._editLocales in Task 5.4) order-insensitively.
        // If unchanged, OMIT `locales` from the PATCH body so the server cannot fan out (no
        // async_task_id). If changed, include the full new array (Req 5.2). This keeps a
        // metadata-only edit a true no-op for member permissions (Req 4.2) while a real locales
        // change triggers the existing fan-out (Req 5.5).
        const localesChanged = !this._sameLocaleSet(locales, this._editLocales)

        this._disableEditSubmit()

        // Build the PATCH body: always send name/description; only send locales when they changed.
        const body = { name, description }
        if (localesChanged) {
            body.locales = locales
        }

        try {
            const response = await fetch(`/api/projects/${ encodeURIComponent(projectId) }`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify(body),
                credentials: 'same-origin'
            })

            if (!response.ok) {
                // Surface the wrapped service error (duplicate name, blank/too-long name,
                // invalid/empty locale) as a failure toast (Req 4.3, 4.4, 10.1).
                const data = await response.json().catch(() => ({}))
                const message = data?.message || data?.errors?.[0]?.message || 'Failed to update project.'
                sendFailureNotification(message)
                this._enableEditSubmit()
                return
            }

            const data = await response.json()
            const project = data?.data || data

            // When the locales changed the server fans out to every member's materialized project
            // policy entry and returns an async_task_id (Req 5.5). Read it from the wrapped success
            // envelope (it may live on the project payload or at the top level depending on the
            // endpoint's shape).
            const asyncTaskId = (project && project.async_task_id) || (data && data.async_task_id) || null

            // Success toast (Req 10.3). When a locales change fanned out to member permissions
            // (Req 5.6, 10.4), the server returns an async_task_id; fold the propagation note into
            // the same success toast so the admin learns about it without an inline notice (the
            // modal closes on success and a separate in-modal region would vanish with it).
            const projectName = (project && project.name) || name
            const successText = asyncTaskId
                ? `Project "${ projectName }" updated. Member permission updates are propagating…`
                : `Project "${ projectName }" updated successfully.`
            sendSuccessNotification(successText)

            // Refresh the store-backed list so the row reflects the new name/description/locale
            // count without a full page reload (reuses the create flow's mechanism).
            this._refreshProjectsList(project)

            // Always close the edit modal on success (Req 4.5, 10.3).
            this._closeEditModal()
        } catch (error) {
            console.error('Error updating project:', error)
            sendFailureNotification('A network error occurred. Please try again.')
        } finally {
            this._enableEditSubmit()
        }
    }

    // --- Edit-form helpers (Task 7) ---

    _closeEditModal() {
        const target = this.hasEditModalTarget ? this.editModalTarget : null
        if (!target) {
            return
        }
        const bs = window.bootstrap
        if (bs && bs.Modal) {
            bs.Modal.getOrCreateInstance(target).hide()
        }
    }

    _disableEditSubmit() {
        this._editSaveInFlight = true
        if (this.hasEditSubmitButtonTarget) {
            this.editSubmitButtonTarget.disabled = true
            this.editSubmitButtonTarget.textContent = 'Saving...'
        }
    }

    _enableEditSubmit() {
        this._editSaveInFlight = false
        if (this.hasEditSubmitButtonTarget) {
            // If facilities are still loading, keep Save disabled (the loading gate owns it);
            // only restore the enabled state when loading is not blocking it.
            this.editSubmitButtonTarget.disabled = !!this._editFacilitiesLoading
            this.editSubmitButtonTarget.textContent = 'Save Changes'
        }
    }

    /**
     * Order-insensitive set comparison of two locale arrays (Req 5.7). Used to decide whether the
     * edit form's selected locales differ from the project's originally-loaded locales; when they
     * are equal we omit `locales` from the PATCH body so no fan-out can occur. Values are compared
     * as strings and de-duplicated so ordering, duplicates, and numeric/string differences do not
     * register as a change.
     */
    _sameLocaleSet(a, b) {
        const setA = new Set((Array.isArray(a) ? a : []).filter(v => v != null).map(v => String(v)))
        const setB = new Set((Array.isArray(b) ? b : []).filter(v => v != null).map(v => String(v)))
        if (setA.size !== setB.size) {
            return false
        }
        for (const value of setA) {
            if (!setB.has(value)) {
                return false
            }
        }
        return true
    }

    // --- Member add/remove flow (Task 9) ---
    //
    // Add and remove members on the project currently open in the edit modal. Both mutations
    // are owned by this management controller (not the supplier), reuse the existing members
    // panel loader (_loadMembers) to re-render the panel + refresh the add-member candidate
    // options, and refresh the per-row member count in the list after the change. Errors from
    // the service (already-a-member on add, last-member on remove) are surfaced as a failure
    // toast and the offending change is NOT applied
    // (Req 6.3, 6.5, 10.1).
    //
    // CSRF/credentials match createProject/updateProject: X-CSRF-Token header via getCsrfToken()
    // and credentials: 'same-origin'. The DELETE carries the CSRF header too.

    /**
     * Add the user selected in the edit modal's add-member single-select to the current project.
     *
     * Reads the target project from editProjectIdInput and the selected user id from
     * this.addMemberTomSelect (single-select, Task 5.6). Guards an empty selection with a members
     * error and no request. POSTs /api/projects/:project_id/members with { user_id }. On success:
     * clears the add-member selection, reloads the members panel via _loadMembers (which
     * re-renders the list, toggles the empty state, and refreshes the add-member options to
     * exclude current members), and refreshes the per-row member count for that project. On a 4xx
     * (e.g. "already a member"), surfaces the wrapped server error as a failure toast and
     * adds no duplicate (Req 6.2, 6.3, 6.6, 10.1).
     */
    async addMember(event) {
        if (event) {
            event.preventDefault()
        }

        const projectId = this.hasEditProjectIdInputTarget ? this.editProjectIdInputTarget.value : ''
        if (!projectId) {
            sendFailureNotification('Unable to determine which project to add a member to.')
            return
        }

        // Selected user id from the store-backed add-member single-select (Task 5.6).
        const userId = this.addMemberTomSelect ? this.addMemberTomSelect.getValue() : ''
        if (!userId) {
            sendFailureNotification('Select a member to add.')
            return
        }

        try {
            const response = await fetch(
                `/api/projects/${ encodeURIComponent(projectId) }/members`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': getCsrfToken()
                    },
                    body: JSON.stringify({ user_id: userId }),
                    credentials: 'same-origin'
                }
            )

            if (!response.ok) {
                // Surface the wrapped service error (e.g. "already a member") and add no duplicate
                // (Req 6.3, 10.1).
                const data = await response.json().catch(() => ({}))
                const message = data?.message || data?.errors?.[0]?.message || 'Failed to add member.'
                sendFailureNotification(message)
                return
            }

            // Clear the picked user, then reload the members panel (re-renders the list, toggles
            // the empty state, and refreshes the add-member candidates to exclude current members)
            // and refresh the per-row member count (Req 6.2, 6.6).
            if (this.addMemberTomSelect) {
                this.addMemberTomSelect.clear(true)
            }
            await this._loadMembers(projectId)
            this._refreshMemberCountFromEditMembers(projectId)
        } catch (error) {
            console.error('Error adding project member:', error)
            sendFailureNotification('A network error occurred adding the member. Please try again.')
        }
    }

    /**
     * Remove a member from the project. The per-row remove button (rendered by _memberRowHtml in
     * Task 5.4) carries data-user-id and data-project-id; both are read from
     * event.currentTarget.dataset. DELETEs /api/projects/:project_id/members/:user_id. On success:
     * reloads the members panel via _loadMembers and refreshes the per-row member count. On a 4xx
     * (e.g. "last member" — "A project must have at least one member"), surfaces the wrapped server
     * error as a failure toast and does NOT remove the row (Req 6.4, 6.5, 6.6, 10.1).
     */
    async removeMember(event) {
        if (event) {
            event.preventDefault()
        }

        const dataset = event?.currentTarget?.dataset || {}
        const projectId = dataset.projectId || ''
        const userId = dataset.userId || ''

        if (!projectId || !userId) {
            sendFailureNotification('Unable to determine which member to remove.')
            return
        }

        // Defensive guard: admin rows render the remove button disabled with no removeMember
        // action, so this normally can't fire for them. Should it ever be reached (e.g. the DOM
        // is tampered with), no-op — admins can't be removed and the server would reject it anyway.
        const members = this._editMembers && Array.isArray(this._editMembers.members) ? this._editMembers.members : []
        const target = members.find(m => m && String(m.user_id) === String(userId))
        if (target && target.admin) {
            return
        }

        try {
            const response = await fetch(
                `/api/projects/${ encodeURIComponent(projectId) }/members/${ encodeURIComponent(userId) }`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': getCsrfToken()
                    },
                    credentials: 'same-origin'
                }
            )

            if (!response.ok) {
                // Surface the wrapped service error (e.g. last-member) and leave the row in place
                // (Req 6.5, 10.1). DELETE success is 204 No Content (no JSON body); errors carry
                // the standard wrapped JSON error.
                const data = await response.json().catch(() => ({}))
                const message = data?.message || data?.errors?.[0]?.message || 'Failed to remove member.'
                sendFailureNotification(message)
                return
            }

            // Reload the members panel (re-renders the list + refreshes the add-member candidates)
            // and refresh the per-row member count (Req 6.4, 6.6).
            await this._loadMembers(projectId)
            this._refreshMemberCountFromEditMembers(projectId)
        } catch (error) {
            console.error('Error removing project member:', error)
            sendFailureNotification('A network error occurred removing the member. Please try again.')
        }
    }

    /**
     * Refresh the list's per-row member count for a project after an add/remove, using the freshly
     * reloaded members panel as the source of truth (Req 6.6). _loadMembers has already populated
     * this._editMembers = { projectId, members } for the open project; adopt its member user ids
     * as the authoritative cache, then render the count as the union with org admins (the same
     * logic the column uses elsewhere) so the number stays consistent with the modal. Guarded so a
     * stale/mismatched edit state can't clobber another row's count.
     */
    _refreshMemberCountFromEditMembers(projectId) {
        if (!this._editMembers || String(this._editMembers.projectId) !== String(projectId)) {
            return
        }
        const members = Array.isArray(this._editMembers.members) ? this._editMembers.members : []
        this._memberUserIds[projectId] = members
            .map((member) => (member && member.user_id != null ? String(member.user_id) : null))
            .filter((id) => id != null)
        this._updateMemberCountCell(projectId, this._memberCountFor({ id: projectId }))
    }

    // --- Delete flow (Task 10) ---
    //
    // Confirm-delete handler for the delete-confirmation modal (opened by openDeleteModal, which
    // seeds deleteProjectIdInput + deleteProjectName). Reads the target project id from the hidden
    // deleteProjectIdInput and DELETEs /api/projects/:id (CSRF via X-CSRF-Token, credentials
    // same-origin). Success is 204 No Content (no JSON body).
    //
    // Errors (Req 7.3/7.4): a 4xx (last-project "Cannot delete the last project", or
    // project-with-items "Cannot delete a project that contains items") is surfaced as a failure
    // toast and the row is left in place with the modal open —
    // the row is only removed on a successful delete (Req 7.6).
    //
    // List-removal approach (Req 7.6): on success we remove the project from allProjectsStore IN
    // PLACE (setAllProjectsStoreValue(filter(...))). This re-renders the table via
    // onAllProjectsStoreUpdate immediately, giving instant feedback without a full page reload and
    // without waiting on the supplier. The projects supplier remains authoritative for subsequent
    // loads (a later refresh would simply not include the deleted project); we prefer the in-place
    // removal here for responsiveness rather than re-triggering the supplier. We also drop any
    // cached per-row member count for the removed project so it can't leak onto a re-used id.
    //
    // Active-project handling (Req 7.7): the admin's active project lives in the server session
    // (session[:active_project_id]) and is mirrored to CurrentProject; there is no client endpoint
    // that clears it (Api::Projects::ContextController#update only SETS it). The mechanism that
    // reuses ProjectContext#clear_active_project semantics from the client is GET /projects/select
    // (Projects::SelectionController#show calls clear_active_project and renders the selection
    // page). So when the deleted id equals the forwarded activeProjectIdValue, we redirect to
    // /projects/select AFTER a successful delete; that clears the session active-project context
    // and prompts the admin to select a project where required (matching design.md's
    // "clears/redirects active-project context"). When the deleted project is NOT the active one,
    // we stay on the management page and just remove the row.
    async confirmDelete(event) {
        if (event) {
            event.preventDefault()
        }

        const id = this.hasDeleteProjectIdInputTarget ? this.deleteProjectIdInputTarget.value : ''
        if (!id) {
            sendFailureNotification('Unable to determine which project to delete.')
            return
        }

        try {
            const response = await fetch(`/api/projects/${ encodeURIComponent(id) }`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                credentials: 'same-origin'
            })

            if (!response.ok) {
                // Surface the wrapped service error (last-project Req 7.3, project-with-items
                // Req 7.4) as a failure toast and leave the row + modal in place
                // (Req 7.3, 7.4, 10.1). DELETE errors carry the standard wrapped JSON error;
                // a 204 success has no body.
                const data = await response.json().catch(() => ({}))
                const message = data?.message || data?.errors?.[0]?.message || 'Failed to delete project.'
                sendFailureNotification(message)
                return
            }

            // Success (Req 7.6): remove the project from the store in place (re-renders the table
            // via onAllProjectsStoreUpdate) and drop any cached member count for it.
            const current = Array.isArray(this.allProjectsStoreValue) ? this.allProjectsStoreValue : []
            this.setAllProjectsStoreValue(current.filter(p => p && String(p.id) !== String(id)))
            if (this._memberUserIds) {
                delete this._memberUserIds[id]
            }

            this._closeDeleteModal()

            sendSuccessNotification('Project deleted successfully.')

            // Active-project handling (Req 7.7): if the deleted project was the admin's active
            // project, redirect to /projects/select, which clears the session active-project
            // context (ProjectContext#clear_active_project semantics) and prompts selection.
            if (this._isActiveProject(id)) {
                window.location.assign('/projects/select')
            }
        } catch (error) {
            console.error('Error deleting project:', error)
            sendFailureNotification('A network error occurred deleting the project. Please try again.')
        }
    }

    // --- Delete-flow helpers (Task 10) ---

    /**
     * True when the given project id is the admin's currently-active project (as forwarded on the
     * page root element via activeProjectIdValue). Guards against a blank active id so a page with
     * no active project never triggers the redirect.
     */
    _isActiveProject(id) {
        const activeId = this.hasActiveProjectIdValue ? this.activeProjectIdValue : ''
        return !!activeId && String(activeId) === String(id)
    }

    _closeDeleteModal() {
        const target = this.hasDeleteModalTarget ? this.deleteModalTarget : null
        if (!target) {
            return
        }
        const bs = window.bootstrap
        if (bs && bs.Modal) {
            bs.Modal.getOrCreateInstance(target).hide()
        }
    }

    // --- Helpers ---

    _showModal(target) {
        if (!target) {
            return
        }
        const bs = window.bootstrap
        if (bs && bs.Modal) {
            bs.Modal.getOrCreateInstance(target).show()
        }
    }

    _escapeHtml(text) {
        const div = document.createElement("div")
        div.textContent = text == null ? "" : String(text)
        return div.innerHTML
    }

    _escapeAttr(text) {
        return this._escapeHtml(text).replace(/"/g, "&quot;")
    }

    _cssEscape(value) {
        const str = value == null ? "" : String(value)
        if (window.CSS && typeof window.CSS.escape === "function") {
            return window.CSS.escape(str)
        }
        // Minimal fallback: escape double quotes for the attribute selector.
        return str.replace(/"/g, '\\"')
    }
}
